/**
 * A place's name as it should be read on screen.
 *
 * Users type place names the way they type into a search box: lower case, and often with the spaces left
 * out. The rows this was drawn from include `tiemnuongxomleo`, `chuyencuanang5` and `quan bun bo hue`,
 * and a column of them reads as unformatted data rather than as the trip it is meant to describe. The
 * arrange step runs every name through here before it prints one.
 *
 * **Only the first letter is capitalised, and that is the rule rather than a shortcut.** Vietnamese
 * capitalises the first letter of a proper name and leaves the rest of the name alone — `Bánh mì Hội An`,
 * never `Bánh Mì Hội An` — so a first-letter transform is what the language actually asks for. Per-word
 * Title Case was considered and rejected for three separate reasons. It would fight the language on every
 * multi-word name. It would break the names that carry their own casing, `iPhone` being the obvious one.
 * And for the names the user complained about it would do nothing whatever, because `tiemnuongxomleo` has
 * no interior space to capitalise after — the transform would return the complaint unchanged.
 *
 * Because only the first character is touched, a name that starts with something other than a letter — a
 * digit, a punctuation mark, an emoji — comes back exactly as it went in. That is the honest result:
 * there is no letter there to capitalise, and inventing one would be editing the user's own data.
 */
export function displayName(name: string): string {
  const trimmed = name.trim();

  if (trimmed === "") {
    return "";
  }

  /*
   * A space before a number that runs into a word.
   *
   * `chuyencuanang5` is a shape users type, and the digit is the one word boundary such a string carries: a
   * letter followed by a number is two tokens. Nothing else in it is a break — `tiemnuongxomleo` has no signal
   * at all, and turning that into `Tiệm nướng Xóm Lèo` would take a Vietnamese dictionary and a segmentation
   * model rather than a formatting rule. Written down because this screen was asked for exactly that
   * transformation: this is the part of it that can be done without inventing text the user did not type.
   */
  const spaced = trimmed.replace(/(\p{L})(\d)/gu, "$1 $2");

  // The locale is named rather than left to the host, so that a machine set to Turkish does not capitalise
  // `i` differently here than on the developer's own machine. For Vietnamese the two agree, `đ` to `Đ`
  // included; the argument is there to make that agreement deterministic.
  return spaced.charAt(0).toLocaleUpperCase("vi") + spaced.slice(1);
}
